import { useMemo } from 'react'
import type { FmpFormStep } from '@strata-monorepo/find-my-plan/types/form'
import type { FormStepResult } from '../../types'
import type { ProgressDisplayConfig } from '../../constants/progressDisplayConfig'
import { dynamicProgressCalculator } from '../../utils/dynamicProgressCalculator'

export interface UseQuestionnaireFooterSkipArgs {
  formStep: FmpFormStep
  canSkip?: boolean
  filteredQuestions: FmpFormStep[]
  currentStepIndex: number
  currentStepNumber?: number
  totalSteps?: number
  progressDisplay: ProgressDisplayConfig
  formResults: FormStepResult
}

export interface QuestionnaireFooterSkipState {
  skipQuestionLabel: string | undefined
  effectiveCanSkip: boolean
  footerSkipCompletesQuiz: boolean
}

/**
 * Resolves footer secondary action copy ("Skip" vs "Take Me to Plans") and whether that action
 * completes the quiz (skip-to-results). Last-step detection mixes Odyssey flags, filtered flow
 * position, store index, and — only in quick flow — dynamic N/N counters.
 */
export function useQuestionnaireFooterSkip({
  formStep,
  canSkip,
  filteredQuestions,
  currentStepIndex,
  currentStepNumber,
  totalSteps,
  progressDisplay,
  formResults,
}: UseQuestionnaireFooterSkipArgs): QuestionnaireFooterSkipState {
  const derivedQuickFlowProgress = useMemo(() => {
    if (progressDisplay.mode !== 'step') return null
    const stepIndex = filteredQuestions.findIndex((q) => q.name === formStep.name)
    if (stepIndex < 0) return null
    return new dynamicProgressCalculator().calculate({
      mode: progressDisplay.mode,
      currentStepIndex: stepIndex,
      questions: filteredQuestions,
      formResults,
    })
  }, [filteredQuestions, formStep.name, formResults, progressDisplay.mode])

  return useMemo((): QuestionnaireFooterSkipState => {
    const lastFilteredIndex = filteredQuestions.length - 1
    const currentFilteredIndex = filteredQuestions.findIndex((q) => q.name === formStep.name)

    const isLastStepInFilteredQuiz =
      lastFilteredIndex >= 0 &&
      currentFilteredIndex >= 0 &&
      currentFilteredIndex === lastFilteredIndex

    const isLastStepByStoreIndex =
      typeof currentStepIndex === 'number' &&
      lastFilteredIndex >= 0 &&
      currentStepIndex === lastFilteredIndex

    const storeCountersMatchFinalCoreStep =
      typeof currentStepNumber === 'number' &&
      typeof totalSteps === 'number' &&
      totalSteps > 0 &&
      currentStepNumber === totalSteps

    const derivedCountersMatchFinalCoreStep =
      derivedQuickFlowProgress !== null &&
      typeof derivedQuickFlowProgress.currentStepNumber === 'number' &&
      typeof derivedQuickFlowProgress.totalSteps === 'number' &&
      derivedQuickFlowProgress.totalSteps > 0 &&
      derivedQuickFlowProgress.currentStepNumber === derivedQuickFlowProgress.totalSteps

    const isTakeMeToPlansFooterLabel =
      Boolean(formStep.componentProps?.lastQuestion) ||
      isLastStepInFilteredQuiz ||
      isLastStepByStoreIndex ||
      (progressDisplay.mode === 'step' && storeCountersMatchFinalCoreStep) ||
      derivedCountersMatchFinalCoreStep

    const effectiveCanSkip = Boolean(canSkip) || isTakeMeToPlansFooterLabel

    const skipQuestionLabel = effectiveCanSkip
      ? isTakeMeToPlansFooterLabel
        ? 'Take Me to Plans'
        : 'Skip'
      : undefined

    const footerSkipCompletesQuiz = effectiveCanSkip && isTakeMeToPlansFooterLabel

    return {
      skipQuestionLabel,
      effectiveCanSkip,
      footerSkipCompletesQuiz,
    }
  }, [
    canSkip,
    currentStepIndex,
    currentStepNumber,
    derivedQuickFlowProgress,
    filteredQuestions,
    formStep,
    totalSteps,
    progressDisplay.mode,
  ])
}
